import type { PlacementRow } from '@/api/types';

export interface AggregatedPlacement {
  channel: string;
  placement: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  tickets: number;
  video_starts: number;
  video_completions: number;
  vcr: number | null;
  /**
   * False when any day rolled into this placement can still be restated by the
   * provider — today's delivery, or a day inside the provider's restatement
   * window. Such a row is a real measurement that may still move, so it is
   * rendered as provisional rather than withheld or presented as settled.
   * Null when no scope reported completeness at all.
   */
  is_complete: boolean | null;
}

/** A group is final only when every day rolled into it is. */
function mergeCompleteness(
  current: boolean | null,
  incoming: boolean | null | undefined,
): boolean | null {
  if (incoming === null || incoming === undefined) return current;
  if (current === null) return incoming;
  return current && incoming;
}

/**
 * Collapse daily facts to placement grain. Ratios are always derived after
 * summing their additive numerator and denominator.
 */
export function aggregatePlacements(rows: PlacementRow[]): AggregatedPlacement[] {
  const aggregates = new Map<string, AggregatedPlacement>();
  for (const row of rows) {
    const key = `${row.channel}|${row.placement}`;
    const current = aggregates.get(key) ?? {
      channel: row.channel,
      placement: row.placement,
      impressions: 0,
      clicks: 0,
      spend_cents: 0,
      tickets: 0,
      video_starts: 0,
      video_completions: 0,
      vcr: null,
      is_complete: null,
    };
    current.impressions += Number(row.impressions) || 0;
    current.clicks += Number(row.clicks) || 0;
    current.spend_cents += Number(row.spend_cents) || 0;
    current.tickets += Number(row.tickets) || 0;
    current.video_starts += Number(row.video_starts) || 0;
    current.video_completions += Number(row.video_completions) || 0;
    current.vcr = current.video_starts
      ? current.video_completions / current.video_starts
      : null;
    current.is_complete = mergeCompleteness(current.is_complete, row.is_complete);
    aggregates.set(key, current);
  }
  return Array.from(aggregates.values())
    .sort((a, b) => b.impressions - a.impressions);
}
